import { formatAmount } from '~/shared/lib/formatAmount';
import { CurrencyCode, MonetaryAmount } from '~/shared/types';
import { Spinner } from '~/shared/ui/Spinner/Spinner';

import { useLocalization } from '../../localization/localization';

import { Container, Row, Title, Value } from './PaymentInformationBlock.ui';
import { Label } from './components/Label/Label';

type MonetaryAmountObject = {
  currency: CurrencyCode;
  value: MonetaryAmount;
};

type PaymentInformationBlockProps = {
  rate: null | {
    destinationCurrency: CurrencyCode;
    rate: number;
    sourceCurrency: CurrencyCode;
  };
  steamAccountAmount: null | MonetaryAmountObject;
  userPaymentAmount: null | MonetaryAmountObject;

  isPending?: boolean;
};

export const PaymentInformationBlock = (
  props: PaymentInformationBlockProps,
) => {
  const { isPending, rate, steamAccountAmount, userPaymentAmount } = props;

  const { t } = useLocalization();

  const displayValue = <Value,>(
    value: Value,
    render: (value: NonNullable<Value>) => React.ReactNode,
  ) => {
    if (value !== null && value !== undefined) {
      return render(value);
    }

    if (isPending) {
      return <Spinner />;
    }

    return '—';
  };

  return (
    <div>
      <Title>{t('Payment information')}</Title>

      <Container>
        <Row>
          <Label>{t('Amount to be paid')}</Label>

          <Value>
            {displayValue(userPaymentAmount, ({ currency, value }) =>
              formatAmount({
                amount: value,
                currency,
              }),
            )}
          </Value>
        </Row>

        <Row>
          <Label tooltip={t('The rate may differ at the time of transaction')}>
            {t('USD exchange rate')}
          </Label>

          <Value>
            {displayValue(
              rate,
              ({
                destinationCurrency,
                rate: rateToCalculate,
                sourceCurrency,
              }) =>
                t('conversion rate formula', {
                  source: formatAmount(
                    {
                      amount: 1 as MonetaryAmount,
                      currency: sourceCurrency,
                    },
                    {
                      minimumFractionDigits: 0,
                    },
                  ),
                  destination: formatAmount({
                    amount: rateToCalculate as MonetaryAmount,
                    currency: destinationCurrency,
                  }),
                }),
            )}
          </Value>
        </Row>

        <Row>
          <Label
            tooltip={t(
              'For Russian accounts, conversion from USD to RUB will be inside Steam',
            )}
          >
            {t('It will be credited approximately')}
          </Label>

          <Value>
            {displayValue(steamAccountAmount, ({ currency, value }) =>
              formatAmount({
                amount: value,
                currency,
              }),
            )}
          </Value>
        </Row>
      </Container>
    </div>
  );
};
